/**
 * The pay step the person is in (agent wallet spec 2026-10-15 §2), shared by the purchase card in the task and the
 * banner over the browser pane, so either one's I've paid, Cancel payment and still-open question act on one state.
 * At most one at a time: the server allows one pay step per person.
 *
 * finishPay and cancelPay answer with the server's result, so the card that pressed them can show the purchase the
 * server moved to; the banner ignores it.
 *
 * The pay step keeps the server's purchase it began from, so the banner draws the shop, the purchase and the agent
 * from the server's row without a read of its own. While the banner is up it says so (`barFor`): it owns the focus
 * then, and the card in the task never moves it (one surface owns the focus, never two fighting over it).
 *
 * THE STORE OWNS THE PAY STEP, FROM THE SERVER (P3 task 13 fix round 1). It reads the person's open pay step
 * (GET /api/purchases/active) once when the app loads (PayStepSync), whenever a browser pane mounts (watchPayStep),
 * when a card's read disagrees with it (syncPayStep), and every PAY_STEP_POLL_MS while a pane is up or a pay step is
 * known: one poller, so a step a sweep ended goes within one poll, and a reload onto any task learns an open one.
 * Every read is numbered: a read sent before a local start or end (or a sign-out), or older than one that already
 * landed, says nothing; a slow read is never cancelled by a newer one still in flight (fix round 3).
 * A purchase whose step ended here (I've paid or Cancel went through, or the server said it was over) never starts a
 * bar again, whatever a late read says. The only other start is Approve's own answer (startPay). `payEndedAt` keeps
 * the browser's frames blocked PAY_END_GRACE_MS after a step ends (the runner covers and closes the page after the
 * route answers).
 *
 * FRAMES FAIL CLOSED (fix rounds 2, 3): `payClear` is the latest read that came back and said no step is open: its
 * number (payReadsSent() counts the reads sent), when it came back, and how long ago the server says the person's
 * last step ended. A pane holds each frame it captures and saves it only once a read sent after the capture says
 * that, and the last end is older than the frame plus the grace (components/browser/pay-step.ts usePayFrameGate);
 * while reads fail, nothing is saved. Sign-out (reset) stamps payEndedAt, which drops every held frame. Every time
 * the frame rules compare here is on performance.now() (fix round 4): a wall-clock step can never loosen them; and
 * a frame's age against the server's span is the larger of its monotonic and wall-clock ages (fix round 5), so a
 * sleep, which stops performance.now(), cannot loosen them either.
 *
 * It also holds the person's budgets (P1's GET /api/agent-budgets) for every purchase card and receipt in view: one
 * read, kept BUDGETS_TTL_MS, shared by them all, read again (force) after a verdict moves the money, and read again
 * when a card needs one taken after a moment (newerThan: a paid receipt's settlement), whatever its age. `budgetsAt`
 * is when the read was SENT, so a read sent before the money settled never passes for one after it.
 *
 * reset() empties it at sign-out (AuthContext.signOut): the next person on this browser starts with nothing, and a
 * read still in flight from before lands nowhere.
 */
import { create } from "zustand";
import type {
  AgentBudgetCurrency,
  AgentBudgetView,
  AgentPurchaseView,
} from "@bash/shared";
import { PAY_STEP_SCREEN_GRACE_MS } from "@bash/shared";
import { getAgentBudgets } from "@/lib/api/agent-budgets";
import {
  declinePurchase,
  finishPayStep,
  getActivePayStep,
  type ActivePayStep,
  type PurchaseActionResult,
} from "@/lib/api/purchases";

/** How long one budgets read serves every card in view. */
export const BUDGETS_TTL_MS = 30_000;
/** How often the store reads the person's open pay step while a browser pane is up or a pay step is known. */
export const PAY_STEP_POLL_MS = 15_000;
/** How long the browser's frames stay blocked after a pay step ends here: longer than the 10 s frame tick. */
export const PAY_END_GRACE_MS = PAY_STEP_SCREEN_GRACE_MS;

export interface ActivePay {
  purchaseId: string;
  taskId: string;
  /** The server's purchase the pay step began from: its shop, details and agent, for the pay banner. */
  purchase: AgentPurchaseView;
  approvedMinor: number;
  currency: AgentBudgetCurrency;
  upiLink: string | null;
  stillOpen: boolean;
  failed: boolean;
}

/** A read that came back saying no pay step is open. */
export interface PayClear {
  /** Its number (payReadsSent when it was sent). */
  seq: number;
  /** When it came back, on this tab's monotonic clock (performance.now()). */
  receivedAt: number;
  /**
   * When it came back on the wall clock (Date.now()): performance.now() can stand still while the computer sleeps,
   * the wall clock does not, so a frame's age is the larger of the two spans (pay-step.ts).
   */
  receivedWall: number;
  /** How long before it the person's last pay step ended, on the server's clock; null if they never had one. */
  lastEndedMsAgo: number | null;
}

interface PurchaseState {
  activePay: ActivePay | null;
  /** The purchase whose pay banner is up over the browser pane, or null. The banner owns the focus while it is. */
  barFor: string | null;
  /**
   * When a pay step last ended here, on performance.now() (-Infinity never): the browser's frames stay blocked
   * PAY_END_GRACE_MS after it.
   */
  payEndedAt: number;
  /** The latest read that came back and said no pay step is open; null none yet (or since sign-out). */
  payClear: PayClear | null;
  /** The person's budgets; null until read, [] while the wallet is dark. */
  budgets: AgentBudgetView[] | null;
  /** When the standing read was sent (ms); 0 never. */
  budgetsAt: number;
  /**
   * Read them unless a read younger than BUDGETS_TTL_MS stands. `force` reads again now (after a verdict);
   * `newerThan` reads again unless the standing read was sent at or after that moment.
   */
  loadBudgets: (opts?: { force?: boolean; newerThan?: number }) => Promise<void>;
  /**
   * A budgets read lands: the store's own (loadBudgets) and Settings' (agent wallet P3 task 14). Only a read sent in
   * this session (`from`, budgetsSession() when it was sent) and at or after the standing one (`sentAt`) lands.
   */
  landBudgets: (
    budgets: AgentBudgetView[],
    sentAt: number,
    from: number
  ) => void;
  /** Sign-out: no pay step, no budgets. */
  reset: () => void;
  startPay: (p: AgentPurchaseView) => void;
  finishPay: (
    purchaseId: string,
    force: boolean
  ) => Promise<PurchaseActionResult>;
  cancelPay: (purchaseId: string) => Promise<PurchaseActionResult>;
  keepPaying: (purchaseId: string) => void;
  setUpiLink: (purchaseId: string, link: string) => void;
  clear: (purchaseId?: string) => void;
  /** The pay banner says it is up (on mount) or gone (on unmount); a banner that goes clears only its own purchase. */
  showBar: (purchaseId: string, shown: boolean) => void;
  /** Read the person's open pay step from the server and take it as the pay step (or none). A failed read changes nothing. */
  syncPayStep: () => Promise<void>;
  /** A browser pane is up: read now, then every PAY_STEP_POLL_MS while one is up or a pay step is known. Returns the release. */
  watchPayStep: () => () => void;
}

/** A code that means the pay step is already over: nothing left to show. */
const OVER = new Set(["not_waiting", "not_found"]);

/** The sign-in session a budgets read is sent in: landBudgets takes it back, so a read from before a sign-out drops. */
export const budgetsSession = (): number => session;

/** A budgets read and when it was sent. */
type BudgetsRead = { sentAt: number; done: Promise<void> };
/** The one budgets read in flight, so cards that mount together share it. */
let budgetsRead: BudgetsRead | null = null;
/** Bumped by reset(): a read sent before a sign-out never lands after it. */
let session = 0;
/** Bumped by every LOCAL start or end of the pay step (and sign-out): a read sent before one never lands after it. */
let localMark = 0;
/** The number of the newest read that landed: an older one landing later says nothing. */
let landedSeq = 0;
/** Purchases whose pay step ended here: none of them starts a bar again. */
const endedHere = new Set<string>();
/** Pay-step reads sent so far: a held frame waits for a clean read numbered above the count at its capture. */
let readsSent = 0;
/** Browser panes watching the pay step. */
let watchers = 0;
/** The one pay-step poller. */
let poll: ReturnType<typeof setInterval> | null = null;

/** How many pay-step reads have been sent: a frame captured now waits for a clean read numbered above this. */
export const payReadsSent = (): number => readsSent;

export const usePurchaseStore = create<PurchaseState>((set, get) => {
  const mine = (purchaseId: string) => {
    const cur = get().activePay;
    return cur?.purchaseId === purchaseId ? cur : null;
  };
  /** The poller runs while a pane is up or a pay step is known, and only then. */
  const keepPolling = () => {
    const want = watchers > 0 || get().activePay !== null;
    if (want && !poll)
      poll = setInterval(() => void get().syncPayStep(), PAY_STEP_POLL_MS);
    else if (!want && poll) {
      clearInterval(poll);
      poll = null;
    }
  };
  const begin = (p: AgentPurchaseView) => {
    set({
      activePay: {
        purchaseId: p.id,
        taskId: p.taskId,
        purchase: p,
        approvedMinor: p.amountMinor,
        currency: p.currency,
        upiLink: null,
        stillOpen: false,
        failed: false,
      },
    });
    keepPolling();
  };
  /** The pay step is over; `here` remembers a purchase whose step ended here, so no late read restarts it. */
  const end = (here?: string) => {
    if (here) endedHere.add(here);
    if (get().activePay && (!here || mine(here)))
      set({ activePay: null, payEndedAt: performance.now() });
    keepPolling();
  };
  return {
    activePay: null,
    barFor: null,
    payEndedAt: Number.NEGATIVE_INFINITY,
    payClear: null,
    budgets: null,
    budgetsAt: 0,
    loadBudgets: async ({ force = false, newerThan } = {}) => {
      // A read is fresh when it was sent at or after `newerThan`; without one, when it is younger than the TTL.
      const fresh = (sentAt: number) =>
        newerThan === undefined
          ? Date.now() - sentAt < BUDGETS_TTL_MS
          : sentAt >= newerThan;
      if (budgetsRead) {
        // A forced read waits out the one in flight (sent before the money moved), then reads again.
        if (!force && fresh(budgetsRead.sentAt)) return budgetsRead.done;
        await budgetsRead.done;
      }
      if (!force && get().budgetsAt > 0 && fresh(get().budgetsAt)) return;
      const sentAt = Date.now();
      const mine = session;
      const read: BudgetsRead = {
        sentAt,
        done: getAgentBudgets()
          .then((s) =>
            get().landBudgets(s.available ? s.body.budgets : [], sentAt, mine)
          )
          // Only the bar reads them: without them the card says what is left in words, and the next card reads again.
          .catch(() => {})
          .finally(() => {
            if (budgetsRead === read) budgetsRead = null;
          }),
      };
      budgetsRead = read;
      return read.done;
    },
    landBudgets: (budgets, sentAt, from) => {
      if (from !== session || sentAt < get().budgetsAt) return;
      set({ budgets, budgetsAt: sentAt });
    },
    reset: () => {
      session += 1;
      localMark += 1;
      budgetsRead = null;
      endedHere.clear();
      set({ activePay: null, budgets: null, budgetsAt: 0 });
      // Every frame a pane holds is dropped (an ending at or after its capture), and the next person starts clean.
      set({ payEndedAt: performance.now(), payClear: null });
      keepPolling();
    },
    startPay: (p) => {
      if (mine(p.id) || endedHere.has(p.id)) return;
      localMark += 1;
      begin(p);
    },
    finishPay: async (purchaseId, force) => {
      const r = await finishPayStep(purchaseId, force);
      if (r.ok || OVER.has(r.code)) {
        localMark += 1;
        end(purchaseId);
        return r;
      }
      const cur = mine(purchaseId);
      if (!cur) return r;
      if (r.code === "still_on_payment_page")
        set({ activePay: { ...cur, stillOpen: true, failed: false } });
      else set({ activePay: { ...cur, failed: true } });
      return r;
    },
    cancelPay: async (purchaseId) => {
      const r = await declinePurchase(purchaseId);
      if (r.ok || OVER.has(r.code)) {
        localMark += 1;
        end(purchaseId);
        return r;
      }
      const cur = mine(purchaseId);
      if (cur) set({ activePay: { ...cur, failed: true } });
      return r;
    },
    keepPaying: (purchaseId) => {
      const cur = mine(purchaseId);
      if (cur) set({ activePay: { ...cur, stillOpen: false } });
    },
    setUpiLink: (purchaseId, link) => {
      const cur = mine(purchaseId);
      if (cur) set({ activePay: { ...cur, upiLink: link } });
    },
    // Never remembers the purchase as ended here: a step cleared before it began must still be able to begin.
    clear: (purchaseId) => {
      if (purchaseId && !mine(purchaseId)) return;
      localMark += 1;
      end();
    },
    showBar: (purchaseId, shown) => {
      if (shown) set({ barFor: purchaseId });
      else if (get().barFor === purchaseId) set({ barFor: null });
    },
    syncPayStep: async () => {
      const seq = ++readsSent;
      const mark = localMark;
      const signedIn = session;
      let answer: ActivePayStep | undefined;
      try {
        answer = await getActivePayStep();
      } catch {
        return;
      }
      // No answer is a failed read. A read sent before a local start or end (or a sign-out), or older than one that
      // already landed, says nothing; a newer read never cancels one still in flight.
      if (!answer || mark !== localMark || signedIn !== session) return;
      if (seq <= landedSeq) return;
      landedSeq = seq;
      const open = answer.purchase;
      if (open && !endedHere.has(open.id)) {
        if (!mine(open.id)) begin(open);
        return;
      }
      if (get().activePay) end();
      // Only the server's own "none is open" clears a held frame (a step it still names, ended here, does not).
      if (!open)
        set({
          payClear: {
            seq,
            receivedAt: performance.now(),
            receivedWall: Date.now(),
            lastEndedMsAgo: answer.lastEndedMsAgo,
          },
        });
    },
    watchPayStep: () => {
      watchers += 1;
      void get().syncPayStep();
      keepPolling();
      let watching = true;
      return () => {
        if (!watching) return;
        watching = false;
        watchers -= 1;
        keepPolling();
      };
    },
  };
});
